import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useUserStore } from '@store'
import { Save, AlertCircle } from 'lucide-react'
import { Modal, Button, Alert, Input, Select, Radio } from '@components/common'
import './UserForm.css'

/**
 * User Form Validation Schema (Base)
 */
const baseUserSchema = z.object({
	company_id: z.number().positive('Company is required'),
	role_id: z.number().positive('Role is required'),
	first_name: z.string().min(2, 'First name must be at least 2 characters').max(100),
	last_name: z.string().min(2, 'Last name must be at least 2 characters').max(100),
	email: z.string().email('Invalid email address'),
	phone: z.string().optional(),
	status: z.enum(['active', 'inactive', 'suspended'], {
		errorMap: () => ({ message: 'Status must be active, inactive, or suspended' }),
	}),
})

/**
 * Create User Schema - Password REQUIRED
 */
const createUserSchema = baseUserSchema.extend({
	password: z.string().min(8, 'Password must be at least 8 characters'),
})

/**
 * Edit User Schema - Password OPTIONAL
 */
const editUserSchema = baseUserSchema.extend({
	password: z.string().optional(),
})

/**
 * User Form Component
 *
 * Universal form for creating and editing users
 *
 * Features:
 * - React Hook Form for form management
 * - Zod for validation
 * - Real-time validation
 * - Loading states
 * - Error handling
 * - Accessible
 *
 * @param {string} mode - 'create' or 'edit'
 * @param {Function} onClose - Close modal callback
 */
const UserForm = ({ mode = 'create', onClose }) => {
	const { selectedUser, companies, roles, createUser, updateUser } = useUserStore()

	const [isSubmitting, setIsSubmitting] = useState(false)
	const [submitError, setSubmitError] = useState(null)

	const isEditMode = mode === 'edit'

	// Select the appropriate schema based on mode
	const validationSchema = isEditMode ? editUserSchema : createUserSchema

	// Initialize form with React Hook Form + Zod
	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		watch,
	} = useForm({
		resolver: zodResolver(validationSchema),
		mode: 'onChange',
		defaultValues: {
			company_id: selectedUser?.company_id ? Number(selectedUser.company_id) : 0,
			role_id: selectedUser?.role_id ? Number(selectedUser.role_id) : 0,
			first_name: selectedUser?.first_name || '',
			last_name: selectedUser?.last_name || '',
			email: selectedUser?.email || '',
			phone: selectedUser?.phone || '',
			password: '',
			status: selectedUser?.status || 'active',
		},
	})

	// Filter active roles
	const activeRoles = roles.filter((role) => role.status === 'active')

	// Handle form submission
	const onSubmit = async (data) => {
		setIsSubmitting(true)
		setSubmitError(null)

		try {
			const formData = {
				company_id: Number(data.company_id),
				role_id: Number(data.role_id),
				first_name: data.first_name,
				last_name: data.last_name,
				email: data.email,
				phone: data.phone || '',
				status: data.status,
			}

			// Only include password in create mode
			if (!isEditMode && data.password) {
				formData.password = data.password
			}

			if (isEditMode) {
				await updateUser(selectedUser.id, formData)
			} else {
				await createUser(formData)
			}

			onClose()
		} catch (error) {
			console.error('Form submission error:', error)
			setSubmitError(error.message || 'Failed to save user')
		} finally {
			setIsSubmitting(false)
		}
	}

	const modalTitle = isEditMode ? 'Edit User' : 'Create New User'

	const footer = (
		<>
			<Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
				Cancel
			</Button>
			<Button
				variant="primary"
				onClick={handleSubmit(onSubmit)}
				loading={isSubmitting}
				disabled={!isDirty && isEditMode}
			>
				<Save size={18} />
				<span>{isEditMode ? 'Update User' : 'Create User'}</span>
			</Button>
		</>
	)

	return (
		<Modal
			isOpen={true}
			onClose={onClose}
			title={modalTitle}
			size="large"
			footer={footer}
			closeOnEscape={false}
			closeOnBackdrop={false}
		>
			<form className="user-form" onSubmit={handleSubmit(onSubmit)}>
				{submitError && (
					<Alert variant="error" message={submitError} dismissible onClose={() => setSubmitError(null)} />
				)}

				{/* Company & Role */}
				<div className="user-form__section">
					<h3 className="user-form__section-title">Organization & Role</h3>

					<div className="user-form__grid">
						<div className="form-group">
							<Select
								label="Company"
								placeholder="Select Company"
								required
								options={[
									{ value: 0, label: 'Select Company' },
									...companies.map((company) => ({
										value: company.id,
										label: company.name
									}))
								]}
								error={errors.company_id?.message}
								disabled={isSubmitting}
								{...register('company_id', { valueAsNumber: true })}
							/>
						</div>

						<div className="form-group">
							<Select
								label="Role"
								placeholder="Select Role"
								required
								options={[
									{ value: 0, label: 'Select Role' },
									...activeRoles.map((role) => ({
										value: role.id,
										label: role.name
									}))
								]}
								error={errors.role_id?.message}
								disabled={isSubmitting}
								{...register('role_id', { valueAsNumber: true })}
							/>
						</div>
					</div>
				</div>

				{/* Personal Information */}
				<div className="user-form__section">
					<h3 className="user-form__section-title">Personal Information</h3>

					<div className="user-form__grid">
						<div className="form-group">
							<Input
								type="text"
								label="First Name"
								placeholder="e.g., John"
								required
								error={errors.first_name?.message}
								disabled={isSubmitting}
								{...register('first_name')}
							/>
						</div>

						<div className="form-group">
							<Input
								type="text"
								label="Last Name"
								placeholder="e.g., Doe"
								required
								error={errors.last_name?.message}
								disabled={isSubmitting}
								{...register('last_name')}
							/>
						</div>
					</div>

					<div className="user-form__grid">
						<div className="form-group">
							<Input
								type="email"
								label="Email"
								placeholder="e.g., john.doe@company.com"
								required
								error={errors.email?.message}
								disabled={isSubmitting}
								{...register('email')}
							/>
						</div>

						<div className="form-group">
							<Input
								type="tel"
								label="Phone"
								placeholder="e.g., (555) 123-4567"
								error={errors.phone?.message}
								disabled={isSubmitting}
								{...register('phone')}
							/>
						</div>
					</div>
				</div>

				{/* Security */}
				<div className="user-form__section">
					<h3 className="user-form__section-title">Security</h3>

					<div className="user-form__grid">
						{!isEditMode && (
							<div className="form-group">
								<Input
									type="password"
									label="Password"
									placeholder="Minimum 8 characters"
									required
									error={errors.password?.message}
									disabled={isSubmitting}
									{...register('password')}
								/>
							</div>
						)}

						{isEditMode && (
							<div className="form-group">
								<label className="form-label">Password</label>
								<div className="form-hint-box">
									<AlertCircle size={16} />
									<span>Use Reset Password action to change user password</span>
								</div>
							</div>
						)}

						<div className="form-group">
							<Radio
								label="Status"
								name="status"
								required
								options={[
									{ value: 'active', label: 'Active' },
									{ value: 'inactive', label: 'Inactive' },
									{ value: 'suspended', label: 'Suspended' }
								]}
								error={errors.status?.message}
								disabled={isSubmitting}
								value={watch('status')}
								{...register('status')}
							/>
						</div>
					</div>
				</div>
			</form>
		</Modal>
	)
}

export default UserForm
